/**
 * Повідомлення `postMessage` між застосунком і Collabora (`SPEC-012` §7.10;
 * легасі `FileInput.jsx:302-372`).
 *
 * Безпека (N-14): легасі слав у `window.frames[0]` з `targetOrigin = '*'` і
 * приймав повідомлення від будь-кого. Тут приймаємо лише від `contentWindow`
 * нашого `iframe` і лише з origin редактора (з `actionUrl`; у варіанті B — це
 * origin застосунку), а надсилаємо адресно з тим самим `targetOrigin`.
 */

export interface CollaboraMessage {
    MessageId: string
    Values?: Record<string, unknown> | null
}

/** Origin з адреси редактора; зіпсована адреса — `null`. */
export const originOf = (url: string): string | null => {
    try {
        return new URL(url).origin
    } catch {
        return null
    }
}

/** Collabora шле JSON-рядок; приймаємо й об'єкт. Чуже — `null`. */
export const parseCollaboraMessage = (data: unknown): CollaboraMessage | null => {
    let value: unknown = data

    if (typeof data === 'string') {
        try {
            value = JSON.parse(data)
        } catch {
            return null
        }
    }

    if (value === null || typeof value !== 'object') return null

    const message = value as Record<string, unknown>

    if (typeof message['MessageId'] !== 'string') return null

    const values = message['Values']

    return {
        MessageId: message['MessageId'],
        Values: values !== null && typeof values === 'object' ? (values as Record<string, unknown>) : null,
    }
}

/** Чи повідомлення справді від нашого редактора. */
export const isFromEditor = (
    event: Pick<MessageEvent, 'source' | 'origin'>,
    frameWindow: Window | null | undefined,
    editorOrigin: string | null
): boolean => frameWindow !== null && frameWindow !== undefined && event.source === frameWindow && editorOrigin !== null && event.origin === editorOrigin

/** Надіслати повідомлення адресно в `iframe` редактора. */
export const postToEditor = (frameWindow: Window | null | undefined, editorOrigin: string | null, message: CollaboraMessage): boolean => {
    if (!frameWindow || !editorOrigin) return false

    frameWindow.postMessage(JSON.stringify(message), editorOrigin)

    return true
}

export const COLLABORA = {
    /** Документ завантажено: `App_LoadingStatus` з `Status: Document_Loaded` */
    loadingStatus: 'App_LoadingStatus',
    documentLoaded: 'Document_Loaded',
    hostReady: 'Host_PostmessageReady',
    save: 'Action_Save',
    saveResponse: 'Action_Save_Resp',
    close: 'Action_Close',
} as const
